import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    legacyApi,
    PurchaseOrderOverview,
    PurchaseOrder,
    WorkflowEntityDefinition,
    WorkflowEventDefinition,
    WorkflowHistoryEntry,
} from '../../../services/legacyApi';
import { ArrowLeft, Download, Link2, Loader2, MessageCircle, Pencil, Send, Trash2 } from 'lucide-react';
import { useConfirm } from '../../../hooks/use-confirm';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { CompactTable } from '../../../components/ui/CompactTable';
import { Button } from '../../../components/ui/button';
import { getPurchaseOrderStatusMeta } from '../utils/statuses';
import { useAuth } from '../../../context/AuthContext';
import { TableFilterBar } from '../../../components/table-filters/TableFilterBar';
import { useTableFilterState } from '../../../hooks/useTableFilterState';
import { TransitionActionBar } from '../components/TransitionActionBar';
import { StatusGuidePanel } from '../components/StatusGuidePanel';
import { StatusHistoryPanel } from '../components/StatusHistoryPanel';
import { FlowCoachPanel } from '../../../components/workflow/FlowCoachPanel';
import { WorkflowRightSidebar } from '../../../components/workflow/WorkflowRightSidebar';
import { WorkflowRightPanel } from '../../../components/workflow/WorkflowRightPanel';
import { SendPdfModal } from '../../../components/SendPdfModal';
import { openWhatsAppMessage } from '../utils/whatsapp';

export const LegacyPurchaseOrderDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { confirm } = useConfirm();
    const { hasPermission } = useAuth();

    const [order, setOrder] = useState<PurchaseOrder | null>(null);
    const [loading, setLoading] = useState(true);
    const [transitioning, setTransitioning] = useState<string | null>(null);
    const [workflowDefinition, setWorkflowDefinition] = useState<WorkflowEntityDefinition | null>(null);
    const [nextActions, setNextActions] = useState<WorkflowEventDefinition[]>([]);
    const [history, setHistory] = useState<WorkflowHistoryEntry[]>([]);
    const [overview, setOverview] = useState<PurchaseOrderOverview | null>(null);
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({ q: '' });
    const [showSendPdfModal, setShowSendPdfModal] = useState(false);

    const loadPage = async (poId: number) => {
        const [orderData, overviewData, definition, actions, historyResult] = await Promise.all([
            legacyApi.purchasing.getPurchaseOrder(poId),
            legacyApi.purchasing.getOrderOverview(poId).catch(() => null),
            legacyApi.workflow.getDefinition('PO').catch(() => null),
            legacyApi.workflow.getNextActions('PO', poId).catch(() => ({ next_actions: [] })),
            legacyApi.workflow.getHistory('PO', poId).catch(() => ({ history: [] })),
        ]);
        setOrder(orderData);
        setOverview(overviewData);
        setWorkflowDefinition(definition);
        setNextActions((actions as any).next_actions || []);
        setHistory((historyResult as any).history || []);
    };

    useEffect(() => {
        if (!id) return;
        const poId = Number(id);
        setLoading(true);
        loadPage(poId)
            .catch((error: any) => {
                console.error(error);
                toast.error(error?.message || 'Failed to load purchase order');
            })
            .finally(() => setLoading(false));
    }, [id]);

    const reloadOrder = async () => {
        if (!id) return;
        await loadPage(Number(id));
    };

    const handleDownload = () => {
        if (!id) return;
        legacyApi.purchasing.downloadPDF(Number(id));
    };

    const resolveSupplierEmail = async () => {
        if (!order?.supplier_id) return '';
        const suppliers = await legacyApi.suppliers.list();
        return suppliers.find((supplier) => Number(supplier.supplier_id) === Number(order.supplier_id))?.email_address || '';
    };

    const handleSendPdf = async (payload: { to: string; cc?: string; subject?: string; body?: string }) => {
        if (!order) return;
        const result = await legacyApi.purchasing.emailPdf(order.po_id, payload);
        toast.success(result.message || 'Purchase order PDF emailed successfully');
    };

    const handleShareLink = async () => {
        if (!order) return;
        try {
            const result = await legacyApi.purchasing.createShareLink(order.po_id);
            try {
                await navigator.clipboard.writeText(result.share_url);
                toast.success(result.is_new ? 'Purchase order share link created and copied' : 'Purchase order share link copied');
            } catch {
                window.prompt('Copy purchase order share link', result.share_url);
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to create purchase order share link');
        }
    };

    const handleShareWhatsApp = async () => {
        if (!order) return;
        try {
            const result = await legacyApi.purchasing.createShareLink(order.po_id, { channel: 'WHATSAPP' });
            const message = `Please find purchase order ${poNumber}: ${result.share_url}`;
            const suppliers = await legacyApi.suppliers.list();
            const supplier = suppliers.find((entry) => Number(entry.supplier_id) === Number(order.supplier_id));
            const resolved = openWhatsAppMessage(message, supplier);
            if (!resolved.normalizedNumber) {
                toast.warning('No valid supplier WhatsApp number found. Opened WhatsApp without a selected recipient.');
            }
        } catch (error: any) {
            toast.error(error?.message || 'Failed to open WhatsApp share');
        }
    };

    const handleDelete = async () => {
        if (!id) return;
        const ok = await confirm('Delete this purchase order?', {
            variant: 'destructive',
            confirmText: 'Delete',
            cancelText: 'Cancel',
        });
        if (!ok) return;
        try {
            await legacyApi.purchasing.deletePurchaseOrder(Number(id));
            toast.success('Purchase order deleted');
            navigate('/legacy/purchase-orders');
        } catch (error) {
            console.error(error);
            toast.error('Failed to delete purchase order');
        }
    };

    const handleTransition = async (action: string) => {
        if (!id || !order) return;
        setTransitioning(action);
        try {
            await legacyApi.purchasing.transitionPurchaseOrder(Number(id), action as 'approve' | 'cancel' | 'close');
            await reloadOrder();
            toast.success('Purchase order status updated');
        } catch (error: any) {
            toast.error(error?.message || `Failed to ${action} purchase order`);
        } finally {
            setTransitioning(null);
        }
    };

    const columns = [
        { header: 'Material Code', accessor: (item: any) => item.material_code || '-', className: 'font-mono' },
        { header: 'Material', accessor: (item: any) => item.material_name || item.description, className: '' },
        { header: 'Project', accessor: (item: any) => item.project_name || item.project_id || '-', className: 'text-sm text-muted-foreground' },
        { header: 'Unit', accessor: (item: any) => item.unit_name || '-', className: '' },
        { header: 'Qty', accessor: (item: any) => item.quantity, className: 'text-right' },
        { header: 'Unit Price', accessor: (item: any) => item.unit_price, className: 'text-right' },
        { header: 'Sub Total', accessor: (item: any) => (item.sub_total ?? item.quantity * item.unit_price).toFixed(2), className: 'text-right' },
    ];

    const filteredDetails = useMemo(() => {
        const details = order?.details || [];
        const query = state.q.trim().toLowerCase();
        if (!query) return details;
        return details.filter((item: any) =>
            String(item.material_code || '').toLowerCase().includes(query) ||
            String(item.material_name || item.description || '').toLowerCase().includes(query) ||
            String(item.project_name || '').toLowerCase().includes(query)
        );
    }, [order?.details, state.q]);

    const headerTransitionActions = useMemo(
        () =>
            (nextActions || []).filter((action) => {
                const code = String(action.event_code || '').trim().toLowerCase();
                return code === 'approve' || code === 'cancel';
            }),
        [nextActions],
    );

    if (loading) return <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>;
    if (!order) return <div className="p-8 text-center text-red-500">Order not found</div>;

    const subTotal = order.details?.reduce((sum, item) => sum + (item.sub_total ?? (item.quantity * item.unit_price)), 0) || 0;
    const discount = order.discount_amount || 0;
    const vatPercent = order.vat || 0;
    const taxable = Math.max(subTotal - discount, 0);
    const vatAmount = taxable * (vatPercent / 100);
    const grandTotal = order.po_amount ?? taxable + vatAmount;
    const statusMeta = getPurchaseOrderStatusMeta(order.po_status_id, order.status_name);
    const statusDefinition = workflowDefinition?.statuses?.find((status) => status.status_id === (order.po_status_id || 1));
    const poNumber = order.po_number || `HC/PO/${String(order.po_id).padStart(4, '0')}`;
    const poAttachmentName = `${poNumber.replace(/\//g, '_')}.pdf`;

    return (
        <div className="space-y-6 w-full p-6 pb-12">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={() => navigate('/legacy/purchase-orders')}>
                        <ArrowLeft className="h-4 w-4 mr-2" /> Back
                    </Button>
                    <h1 className="text-2xl font-bold">Purchase Order #{order.po_id}</h1>
                </div>
                <div className="flex items-center gap-2">
                    {hasPermission('purchasing.po.edit') && (
                        <Button variant="outline" onClick={() => navigate(`/legacy/purchase-orders/edit/${order.po_id}`)}>
                            <Pencil className="h-4 w-4 mr-2" /> Edit
                        </Button>
                    )}
                    {hasPermission('purchasing.po.pdf') && (
                        <Button variant="outline" onClick={handleDownload}>
                            <Download className="h-4 w-4 mr-2" /> Download PDF
                        </Button>
                    )}
                    {hasPermission('purchasing.po.email') && (
                        <Button variant="outline" onClick={() => setShowSendPdfModal(true)}>
                            <Send className="h-4 w-4 mr-2" /> Send
                        </Button>
                    )}
                    {hasPermission('purchasing.po.share') && (
                        <>
                            <Button variant="outline" onClick={handleShareLink}>
                                <Link2 className="h-4 w-4 mr-2" /> Share Link
                            </Button>
                            <Button variant="outline" onClick={handleShareWhatsApp}>
                                <MessageCircle className="h-4 w-4 mr-2" /> WhatsApp
                            </Button>
                        </>
                    )}
                    {hasPermission('purchasing.po.delete') && (
                        <Button variant="destructive" onClick={handleDelete}>
                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                        </Button>
                    )}
                    {headerTransitionActions.length > 0 && (
                        <>
                            <div className="mx-1 h-6 w-px bg-border" />
                            <TransitionActionBar
                                actions={headerTransitionActions}
                                onAction={handleTransition}
                                disabled={transitioning !== null}
                                busyEventCode={transitioning}
                                precheckEntity="PO"
                                precheckEntityId={order.po_id}
                                actionOrder={['approve', 'cancel']}
                            />
                        </>
                    )}
                </div>
            </div>

            <div className="space-y-6">
                <SendPdfModal
                    open={showSendPdfModal}
                    onOpenChange={setShowSendPdfModal}
                    documentLabel="Purchase Order"
                    documentNumber={poNumber}
                    attachmentName={poAttachmentName}
                    defaultSubject={`Purchase Order ${poNumber}`}
                    defaultBody={`Dear Supplier,\n\nPlease find attached purchase order ${poNumber}.\n\nRegards,\nHADAF COMPOSITES`}
                    onSend={handleSendPdf}
                    resolveDefaultTo={resolveSupplierEmail}
                />
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card>
                            <CardHeader><CardTitle>Supplier Details</CardTitle></CardHeader>
                            <CardContent className="space-y-2">
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Supplier</span>
                                    <span className="font-medium">{order.supplier_name}</span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Quotation Ref</span>
                                    <span className="font-medium">{order.supplier_quotation_no || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Date</span>
                                    <span className="font-medium">{order.po_date ? new Date(order.po_date).toLocaleDateString() : '-'}</span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Delivery Date</span>
                                    <span className="font-medium">{order.delivery_date ? new Date(order.delivery_date).toLocaleDateString() : '-'}</span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Delivery Address</span>
                                    <span className="font-medium text-right max-w-[60%]">{order.delivery_address || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Delivery Phone</span>
                                    <span className="font-medium">{order.delivery_phone_no || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Payment Terms</span>
                                    <span className="font-medium">{order.payment_terms || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Payment Method</span>
                                    <span className="font-medium">{order.payment_method || 'N/A'}</span>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader><CardTitle>Summary</CardTitle></CardHeader>
                            <CardContent className="space-y-2">
                                <div className="text-sm text-muted-foreground">
                                    Status: <span className={`px-2 py-1 rounded-full text-xs ${statusMeta.badgeClass}`}>{statusMeta.label}</span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Sub Total</span>
                                    <span className="font-medium">{subTotal.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Discount</span>
                                    <span className="font-medium">{discount.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">VAT %</span>
                                    <span className="font-medium">{vatPercent.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">VAT Amount</span>
                                    <span className="font-medium">{vatAmount.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between border-b pb-2">
                                    <span className="text-muted-foreground">Grand Total</span>
                                    <span className="font-semibold">{grandTotal.toFixed(2)}</span>
                                </div>
                                {order.remarks && (
                                    <div className="mt-4 p-3 bg-yellow-50 text-yellow-800 text-sm rounded">
                                        Remarks: {order.remarks}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    <Card>
                        <CardHeader><CardTitle>Items</CardTitle></CardHeader>
                        <CardContent>
                            <TableFilterBar
                                searchValue={state.q}
                                onSearchChange={(value) => setFilter('q', value)}
                                searchPlaceholder="Search material code, name, project..."
                                isDirty={isDirty}
                                onClear={clearAll}
                                rightSlot={`${filteredDetails.length} results`}
                            />
                            <div className="mt-3">
                                <CompactTable data={filteredDetails} columns={columns} keyField="po_detail_id" />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <WorkflowRightSidebar mode="overlay" title="PO Workflow & Status">
                    <WorkflowRightPanel title="Workflow & Status" description="Current status, next action, and history">
                        {overview?.flow_guards && (
                            <Card>
                                <CardHeader className="pb-2"><CardTitle className="text-sm">Flow Guards</CardTitle></CardHeader>
                                <CardContent className="space-y-2 text-xs">
                                    <div className="flex items-center justify-between"><span>Next</span><span className="font-medium">{overview.next_action || 'review'}</span></div>
                                    <div className="flex items-center justify-between"><span>GRNs</span><span className="font-medium">{overview.flow_guards.grn_count}</span></div>
                                    <div className="flex items-center justify-between"><span>Bills</span><span className="font-medium">{overview.flow_guards.bill_count}</span></div>
                                    <div className="flex items-center justify-between"><span>Posted Bills</span><span className="font-medium">{overview.flow_guards.posted_bill_count}</span></div>
                                    <div className="flex items-center justify-between"><span>Paid Bills</span><span className="font-medium">{overview.flow_guards.paid_bill_count}</span></div>
                                    <div className="rounded border bg-muted/30 p-2">
                                        <div className="font-medium">Create GRN</div>
                                        <div className="text-muted-foreground">{overview.flow_guards.create_grn_reason}</div>
                                    </div>
                                    <div className="rounded border bg-muted/30 p-2">
                                        <div className="font-medium">Create Bill</div>
                                        <div className="text-muted-foreground">{overview.flow_guards.create_bill_reason}</div>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                        <TransitionActionBar
                            actions={nextActions}
                            onAction={handleTransition}
                            disabled={transitioning !== null}
                            busyEventCode={transitioning}
                            precheckEntity="PO"
                            precheckEntityId={order.po_id}
                            hiddenEventCodes={['approve', 'cancel']}
                        />
                        <StatusGuidePanel
                            statusLabel={statusDefinition?.status_label || statusMeta.label}
                            statusMeaning={statusDefinition?.status_meaning || ''}
                            nextActions={nextActions}
                        />
                        <FlowCoachPanel entity="PO" entityId={order.po_id} />
                        <StatusHistoryPanel history={history} />
                    </WorkflowRightPanel>
                </WorkflowRightSidebar>
            </div>
        </div>
    );
};
